import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';

import { ResourceTag } from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { useAuth } from '../useAuth/useAuth';
import { useListTags, useWorkspaceId } from '../useSwr';
import { StoredResourceTagsContext } from './StoredResourceTagsContext';

const resourceTagsLocalStorageKey = 'ls:filter:resource-tags';

// Create the context
export interface StoredResourceTagsContextType {
  selectedTags: ResourceTag[];
  setSelectedTags: Dispatch<SetStateAction<ResourceTag[]>>;
  selectedTagIds: string[];
}

// Create a provider component
export function StoredResourceTagsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const workspaceId = useWorkspaceId();
  const { session } = useAuth();

  const { data: existingTags, isLoading: isLoadingTags } = useListTags({
    skip: !session,
  });
  const [_selectedTags, _setSelectedTags] = useLocalStorageState<ResourceTag[]>(
    `${resourceTagsLocalStorageKey}:${workspaceId}`,
    []
  );

  const setSelectedTags = useCallback(
    (
      tagsOrUpdater: ResourceTag[] | ((prev: ResourceTag[]) => ResourceTag[])
    ) => {
      _setSelectedTags((prevTags) => {
        const newTags =
          typeof tagsOrUpdater === 'function'
            ? tagsOrUpdater(prevTags)
            : tagsOrUpdater;
        // need to exclude resources from the tag object to
        // avoid serializing it because it's too large for local storage
        return newTags.map((tag) => ({ ...tag, resources: undefined }));
      });
    },
    [_setSelectedTags]
  );

  const value = useMemo(() => {
    const flatTagValues = existingTags?.flatMap((tag) => tag.values) ?? [];

    const selectedTags = isLoadingTags
      ? _selectedTags
      : _selectedTags.filter((tag) =>
          flatTagValues.some((value) => value.id === tag.tag_value_id)
        );

    const selectedTagIds = selectedTags.map((tag) => tag.tag_value_id);

    return { selectedTags, setSelectedTags, selectedTagIds };
  }, [existingTags, isLoadingTags, _selectedTags, setSelectedTags]);

  return (
    <StoredResourceTagsContext.Provider value={value}>
      {children}
    </StoredResourceTagsContext.Provider>
  );
}
